import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import Section from "./Section";

type Props = {
  api: string; // e.g., "https://api.example.com" or ""
  token?: string; // optional bearer token
};

type ExtruderSettings = {
  idle_min?: number;
  running_min?: number;
  hysteresis?: number;
  updated_at?: string;
};

const numberOrEmpty = (v: any) =>
  typeof v === "number" && Number.isFinite(v) ? String(v) : "";

const ExtruderSettingsSection: React.FC<Props> = ({ api, token }) => {
  const [loading, setLoading] = useState(true);
  const [orig, setOrig] = useState<ExtruderSettings | null>(null);

  const [idleMin, setIdleMin] = useState("");
  const [runningMin, setRunningMin] = useState("");
  const [hysteresis, setHysteresis] = useState("");

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const endpoint = (path: string) => `${api}${path}`; // empty api => relative

  // -------- GET /settings/extruders --------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(endpoint("/settings/extruders"), {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const j = await res.json();
        if (!alive) return;
        setOrig(j);
        setIdleMin(numberOrEmpty(j.idle_min));
        setRunningMin(numberOrEmpty(j.running_min));
        setHysteresis(numberOrEmpty(j.hysteresis));
      } catch (e: any) {
        setSnack({
          open: true,
          msg: `Failed to load extruder settings: ${e?.message ?? e}`,
          severity: "error",
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, token]); // re-fetch if base or token changes

  const changed = useMemo(() => {
    if (!orig) return false;
    const i = idleMin === "" ? undefined : Number(idleMin);
    const r = runningMin === "" ? undefined : Number(runningMin);
    const h = hysteresis === "" ? undefined : Number(hysteresis);
    return (
      i !== orig.idle_min || r !== orig.running_min || h !== orig.hysteresis
    );
  }, [orig, idleMin, runningMin, hysteresis]);

  const validationError = useMemo(() => {
    const i = Number(idleMin);
    const r = Number(runningMin);
    const h = Number(hysteresis);
    if (idleMin !== "" && i < 0) return "idle_min must be ≥ 0";
    if (runningMin !== "" && r <= 0) return "running_min must be > 0";
    if (hysteresis !== "" && h < 0) return "hysteresis must be ≥ 0";
    if (idleMin !== "" && runningMin !== "" && r <= i)
      return "running_min must be > idle_min";
    return null;
  }, [idleMin, runningMin, hysteresis]);

  // -------- PATCH /settings/extruders --------
  const save = async () => {
    if (validationError) {
      setSnack({ open: true, msg: validationError, severity: "error" });
      return;
    }
    const body: Record<string, number> = {};
    if (idleMin !== "") body.idle_min = Number(idleMin);
    if (runningMin !== "") body.running_min = Number(runningMin);
    if (hysteresis !== "") body.hysteresis = Number(hysteresis);

    try {
      const res = await fetch(endpoint("/settings/extruders"), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const j = await res.json();
      setOrig(j);
      setIdleMin(numberOrEmpty(j.idle_min));
      setRunningMin(numberOrEmpty(j.running_min));
      setHysteresis(numberOrEmpty(j.hysteresis));
      setSnack({
        open: true,
        msg: "Extruder settings saved",
        severity: "success",
      });
    } catch (e: any) {
      setSnack({
        open: true,
        msg: `Save failed: ${e?.message ?? e}`,
        severity: "error",
      });
    }
  };

  const reset = () => {
    if (!orig) return;
    setIdleMin(numberOrEmpty(orig.idle_min));
    setRunningMin(numberOrEmpty(orig.running_min));
    setHysteresis(numberOrEmpty(orig.hysteresis));
  };

  return (
    <Section
      title="Extruder Settings"
      subtitle="Classify screw speed into Stopped, Idle (Purging), and Running states using boundaries."
      rightAction={
        orig?.updated_at ? (
          <Typography variant="caption" color="text.secondary">
            Updated {new Date(orig.updated_at).toLocaleString()}
          </Typography>
        ) : null
      }
    >
      {loading ? (
        <Box py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="idle_min"
                type="number"
                value={idleMin}
                onChange={(e) => setIdleMin(e.target.value)}
                fullWidth
                helperText="Boundary between Stopped and Idle (Purging)"
                error={!!validationError}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="running_min"
                type="number"
                value={runningMin}
                onChange={(e) => setRunningMin(e.target.value)}
                fullWidth
                helperText="Boundary between Idle (Purging) and Running"
                error={!!validationError}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="hysteresis"
                type="number"
                value={hysteresis}
                onChange={(e) => setHysteresis(e.target.value)}
                fullWidth
                helperText="Optional buffer to reduce flapping"
                error={!!validationError}
              />
            </Grid>
          </Grid>

          <Box mt={2} display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={save}
              disabled={!changed || !!validationError}
            >
              Save
            </Button>
            <Button variant="text" onClick={reset} disabled={!changed}>
              Reset
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default ExtruderSettingsSection;
